import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ShieldCheck } from "lucide-react";
import { api } from "../../lib/api";
import { useStripeMode } from "../../lib/useStripeMode";

const SUCCESS_STATUSES = new Set(["paye", "empreinte_active", "rembourse_partiel", "rembourse"]);
const FAIL_STATUSES = new Set(["echoue", "expire"]);

export default function PaymentReturnPage() {
  const { pathname } = useLocation();
  const isSuccess = pathname.includes("succes");
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: isSuccess ? "polling" : "cancelled", label: "" });
  const stripeMode = useStripeMode();
  const attempts = useRef(0);

  useEffect(() => {
    if (!isSuccess) return;
    if (!sessionId) {
      setState({ phase: "unknown", label: "Session de paiement introuvable." });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      attempts.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (SUCCESS_STATUSES.has(r.data.status)) {
          setState({
            phase: "ok",
            label: r.data.type === "caution"
              ? "Votre empreinte de caution est enregistrée — aucun montant n'a été débité."
              : "Votre paiement est confirmé. Votre réservation est mise à jour.",
          });
          return;
        }
        if (FAIL_STATUSES.has(r.data.status)) {
          setState({ phase: "failed", label: "Le paiement n'a pas abouti. Vous pouvez réessayer depuis votre espace client." });
          return;
        }
      } catch { /* sonde silencieuse */ }
      if (!cancelled && attempts.current < 20) setTimeout(poll, 2000);
      else if (!cancelled) setState({ phase: "unknown", label: "Confirmation en cours de traitement — votre espace client se mettra à jour dès réception." });
    };
    poll();
    return () => { cancelled = true; };
  }, [isSuccess, sessionId]);

  return (
    <div className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center px-6 text-center" data-testid={isSuccess ? "payment-success-page" : "payment-cancel-page"}>
      {!isSuccess ? (
        <>
          <XCircle className="h-12 w-12 text-sage" />
          <h1 className="mt-4 font-heading text-2xl text-deepgreen">Paiement interrompu</h1>
          <p className="mt-3 text-sm text-muted-foreground" data-testid="payment-cancel-text">
            Aucun montant n'a été débité. Vous pouvez reprendre le règlement à tout moment depuis votre espace client.
          </p>
        </>
      ) : state.phase === "polling" ? (
        <>
          <Loader2 className="h-12 w-12 animate-spin text-sage" />
          <h1 className="mt-4 font-heading text-2xl text-deepgreen">Confirmation en cours…</h1>
          <p className="mt-3 text-sm text-muted-foreground" data-testid="payment-status-text">Nous vérifions la transaction auprès du prestataire de paiement sécurisé.</p>
        </>
      ) : state.phase === "ok" ? (
        <>
          <CheckCircle2 className="h-12 w-12 text-sage" />
          <h1 className="mt-4 font-heading text-2xl text-deepgreen">Opération confirmée</h1>
          <p className="mt-3 text-sm text-muted-foreground" data-testid="payment-status-text">{state.label}</p>
        </>
      ) : (
        <>
          <ShieldCheck className="h-12 w-12 text-sage" />
          <h1 className="mt-4 font-heading text-2xl text-deepgreen">{state.phase === "failed" ? "Paiement non abouti" : "Transaction en cours"}</h1>
          <p className="mt-3 text-sm text-muted-foreground" data-testid="payment-status-text">{state.label}</p>
        </>
      )}
      <Link to="/compte" className="btn-primary-ae mt-8 !px-6 !py-3 !text-xs" data-testid="payment-return-account">
        Retour à mon espace client
      </Link>
      <p className="mt-4 text-[11px] text-muted-foreground" data-testid="payment-secure-note">
        {stripeMode === "stripe_test"
          ? "Paiement sécurisé par Stripe — mode TEST, aucune charge réelle. Art Event ne conserve aucune donnée bancaire."
          : "Paiement sécurisé par Stripe. Art Event ne conserve aucune donnée bancaire."}
      </p>
    </div>
  );
}
